import { useWinnerRules } from './WinnerRulesContext.jsx';
import { classifyDashboard } from 'lib/winnerRules.mjs';
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { dashboardService } from 'services';
import { EXTRAS_POLL_MS, preserveCampaignExtras, shouldPollExtras } from 'lib/campaignExtras.mjs';
import { useAuth } from './AuthContext.jsx';

/**
 * The weekly dashboard payload, and which week is being looked at.
 *
 * Replaces four globals from public/index.html (dashData, weekOffset, weekKey,
 * plus the applyDashData/_showLoadError pair). Nearly every view reads this, and
 * changing the week refetches.
 *
 * `safeSummary` / `safeAnalytics` are carried over deliberately: the payload can
 * arrive without those sections while a week is still building, and half the
 * views index straight into them. Returning {} rather than undefined is what
 * stops a blank tab.
 */

const DashDataContext = createContext(null);

// A live read that never answers used to leave the spinner up. Past this, the
// page says the week did not load and offers another try. Long enough for a
// slow account, short enough that a hung read cannot sit there all morning.
const DASH_LOAD_MS = 70000;

function linkAbort(parent, ms) {
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), ms);
  const abort = () => ctrl.abort();
  if (parent) {
    if (parent.aborted) ctrl.abort();
    else parent.addEventListener('abort', abort, { once: true });
  }
  return {
    signal: ctrl.signal,
    finish() {
      clearTimeout(timer);
      parent?.removeEventListener('abort', abort);
    },
  };
}

export function DashDataProvider({ children }) {
  const { isSignedIn } = useAuth();

  const [rawData, setData] = useState(null);
  const { winRoas, winSpend, loaded: rulesLoaded, seedFromDashboard } = useWinnerRules();
  useEffect(() => { seedFromDashboard?.(rawData?.analytics); }, [rawData, seedFromDashboard]);
  const data = useMemo(() => rulesLoaded ? classifyDashboard(rawData, winRoas, winSpend) : rawData, [rawData, winRoas, winSpend, rulesLoaded]);
  const [weekOffset, setWeekOffset] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Guards against a slow response for week N-1 landing after week N and
  // painting the wrong week's numbers.
  const seq = useRef(0);

  const loadedAt = useRef(0);
  // How many times this week has been asked again for the PDP link and the
  // cost-cap button. Reset when they arrive, or when a different week is opened.
  const extrasTries = useRef(0);
  const extrasTimer = useRef(null);
  const weekRef = useRef(weekOffset);
  weekRef.current = weekOffset;

  const load = useCallback(async ({ signal, quiet = false, fresh = false } = {}) => {
    if (!isSignedIn) return;
    const mine = ++seq.current;
    // A background refresh keeps the current figures on screen: no spinner,
    // and a failed refresh leaves the last good week in place.
    if (!quiet) { setLoading(true); setError(null); }
    const linked = linkAbort(signal, DASH_LOAD_MS);
    try {
      const res = await dashboardService.getDashboard({ week: weekOffset, fresh, signal: linked.signal });
      if (mine !== seq.current) return; // a newer request already won
      // A body with no week is a failure (a 401, a 500, a dropped connection),
      // not a week to render. Treating it as data left the page on the spinner.
      if (!res?.weekKey) {
        if (!quiet) setError(typeof res?.error === 'string' && res.error ? res.error : 'Could not load this week.');
        return;
      }
      // A quiet reload can arrive before the ad listing has been copied onto
      // the new rows. Keep the buttons the screen already has.
      setData((prev) => preserveCampaignExtras(prev, res));
      loadedAt.current = Date.now();
      // The week opened before the ad listing finished. Ask for the saved
      // week again — not a new read of every account — until the buttons
      // are on the rows. A week change drops the timer on the floor.
      if (shouldPollExtras(res, extrasTries.current)) {
        const weekAtSchedule = weekOffset;
        extrasTries.current += 1;
        clearTimeout(extrasTimer.current);
        extrasTimer.current = setTimeout(() => {
          if (weekRef.current === weekAtSchedule) load({ quiet: true });
        }, EXTRAS_POLL_MS);
      } else {
        extrasTries.current = 0;
        clearTimeout(extrasTimer.current);
      }
    } finally {
      linked.finish();
      // The seq check is the one that matters. Skipping this when the signal
      // aborted left loading true forever if nothing else took the request over.
      if (mine === seq.current && !quiet) setLoading(false);
    }
  }, [isSignedIn, weekOffset]);

  useEffect(() => () => clearTimeout(extrasTimer.current), [weekOffset]);

  /*
   * The CURRENT week is still accruing, so it is not held for the session.
   * It used to load once at sign-in and never again: the week chart's
   * today-tile then disagreed with the live Today card by however long the
   * tab had been open (1.29x / 18 orders against 1.47x / 20, live Sep 27).
   * Every 3 minutes while the tab is visible, and on coming back to it after
   * a minute away. Past weeks are closed and are left alone.
   */
  useEffect(() => {
    if (!isSignedIn || weekOffset !== 0) return undefined;
    const tick = () => {
      if (document.visibilityState === 'visible') load({ quiet: true });
    };
    const t = setInterval(tick, 3 * 60 * 1000);
    const onVisible = () => {
      if (document.visibilityState === 'visible' && Date.now() - loadedAt.current > 60 * 1000) load({ quiet: true });
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => { clearInterval(t); document.removeEventListener('visibilitychange', onVisible); };
  }, [isSignedIn, weekOffset, load]);

  // Abort on unmount or when the week changes, so a request nobody is waiting
  // for cannot land, and cannot log a "network error" when the page goes away.
  useEffect(() => {
    const ctrl = new AbortController();
    load({ signal: ctrl.signal });
    return () => ctrl.abort();
  }, [load]);

  // Manual retry from a UI control — no signal, it should not be cancelled by
  // the effect that happens to re-run alongside it.
  const reload = useCallback(() => load({ fresh: true }), [load]);

  const changeWeek = useCallback(dir => {
    // 0 is the current week; you cannot look into the future.
    setWeekOffset(prev => Math.min(0, prev + dir));
  }, []);

  const value = useMemo(() => ({
    data,
    loading,
    error,
    reload,
    weekOffset,
    setWeekOffset,
    changeWeek,
    weekKey: data?.weekKey ?? '',
    // The human-readable range — "Aug 3 – Aug 7, 2026" — which is what the
    // topbar and the V3 ticker show. weekKey is the machine key (2026-08-03)
    // and is for requests and React keys, not for reading.
    weekLabel: data?.weekLabel ?? '',
    payConfig: data?.payConfig ?? null,
    daily: data?.daily ?? [],
    campaigns: data?.campaigns ?? [],
    safeSummary: data?.summary ?? {},
    safeAnalytics: data?.analytics ?? {},
  }), [data, loading, error, reload, weekOffset, changeWeek]);

  return <DashDataContext.Provider value={value}>{children}</DashDataContext.Provider>;
}

export function useDashData() {
  const ctx = useContext(DashDataContext);
  if (!ctx) throw new Error('useDashData must be used inside <DashDataProvider>');
  return ctx;
}
